import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, Italic, Strikethrough, Code, List, ListOrdered, 
  Quote, Redo, Undo, Link as LinkIcon, Image as ImageIcon,
  Heading1, Heading2, Heading3, Minus, Code2
} from 'lucide-react';
import { Button } from './ui/button';
import { useCallback, useState, useEffect } from 'react';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 h-8 w-8 ${
      isActive 
        ? 'bg-neon-purple/30 text-neon-purple' 
        : 'text-white/60 hover:text-white hover:bg-white/10'
    }`}
  >
    {children}
  </Button>
);

const MenuBar = ({ editor, onImageUpload, onToggleSource, sourceMode }) => {
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-white/10 bg-white/5 rounded-t-lg">
      <div className="flex gap-1 pr-2 border-r border-white/10">
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </MenuButton>
      </div>

      <div className="flex gap-1 pr-2 border-r border-white/10">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          title="Inline Code"
        >
          <Code className="w-4 h-4" />
        </MenuButton>
      </div>

      <div className="flex gap-1 pr-2 border-r border-white/10">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          <Minus className="w-4 h-4" />
        </MenuButton>
      </div>

      <div className="flex gap-1 pr-2 border-r border-white/10">
        <MenuButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={addImage}
          title="Add Image URL"
        >
          <ImageIcon className="w-4 h-4" />
        </MenuButton>
        {onImageUpload && (
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onImageUpload(file, (url) => {
                  editor.chain().focus().setImage({ src: url }).run();
                });
              }}
            />
            <div className="p-2 h-8 w-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded">
              <ImageIcon className="w-4 h-4" />
              <span className="text-[8px] absolute mt-3 ml-3">+</span>
            </div>
          </label>
        )}
      </div>

      <div className="flex gap-1 pr-2 border-r border-white/10">
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          title="Redo (Ctrl+Y)"
        >
          <Redo className="w-4 h-4" />
        </MenuButton>
      </div>

      {/* Source HTML toggle */}
      {onToggleSource && (
        <div className="flex gap-1 ml-auto">
          <MenuButton
            onClick={onToggleSource}
            isActive={sourceMode}
            title="View HTML source (for pasting raw HTML with inline styles)"
          >
            <Code2 className="w-4 h-4" />
          </MenuButton>
        </div>
      )}
    </div>
  );
};

export function RichTextEditor({ content, onChange, placeholder, onImageUpload }) {
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState(content || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-neon-cyan underline hover:text-neon-purple transition-colors',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Start writing your content...',
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setSourceValue(html);
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none min-h-[300px] p-4 focus:outline-none',
      },
      // Allow ALL HTML attributes (including inline style) to pass through paste
      transformPastedHTML: (html) => html,
    },
    // CRITICAL: parse pasted HTML without stripping inline styles
    parseOptions: {
      preserveWhitespace: 'full',
    },
  }, []);

  // Keep editor in sync if parent updates `content` (e.g., switching between posts)
  useEffect(() => {
    if (editor && content !== undefined && content !== editor.getHTML()) {
      editor.commands.setContent(content || '', false);
      setSourceValue(content || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, editor]);

  const handleToggleSource = () => {
    if (sourceMode) {
      // Leaving source mode → push raw HTML back into editor
      if (editor) {
        editor.commands.setContent(sourceValue, false);
      }
      onChange(sourceValue);
    } else {
      // Entering source mode → grab the current HTML
      if (editor) {
        setSourceValue(editor.getHTML());
      }
    }
    setSourceMode(!sourceMode);
  };

  const handleSourceChange = (e) => {
    const v = e.target.value;
    setSourceValue(v);
    onChange(v);
  };

  return (
    <div className="rich-text-editor border border-white/10 rounded-lg bg-white/5 overflow-hidden">
      {!sourceMode && (
        <MenuBar
          editor={editor}
          onImageUpload={onImageUpload}
          onToggleSource={handleToggleSource}
          sourceMode={sourceMode}
        />
      )}

      {sourceMode && (
        <div className="flex items-center justify-between gap-2 p-2 border-b border-white/10 bg-white/5">
          <span className="text-xs font-mono text-white/60 uppercase tracking-wider px-2">
            HTML Source — inline styles preserved
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleToggleSource}
            className="text-neon-cyan hover:text-white hover:bg-white/10 h-8 text-xs"
            data-testid="exit-source-mode-btn"
          >
            ← Back to Visual
          </Button>
        </div>
      )}

      {sourceMode ? (
        <textarea
          value={sourceValue}
          onChange={handleSourceChange}
          spellCheck={false}
          className="w-full min-h-[400px] p-4 bg-[#0A0A0A] text-white/90 font-mono text-sm leading-relaxed focus:outline-none resize-y"
          placeholder="Paste raw HTML here. Inline <style> attributes will be preserved exactly as written."
          data-testid="source-html-textarea"
        />
      ) : (
        <EditorContent editor={editor} className="rich-text-content" />
      )}
      
      <style>{`
        .rich-text-content .ProseMirror {
          min-height: 300px;
          padding: 1rem;
          color: white;
        }
        .rich-text-content .ProseMirror:focus {
          outline: none;
        }
        .rich-text-content .ProseMirror p.is-editor-empty:first-child::before {
          color: rgba(255, 255, 255, 0.3);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .rich-text-content .ProseMirror h1 {
          font-size: 2rem;
          font-weight: bold;
          margin: 1rem 0;
          color: white;
        }
        .rich-text-content .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0.75rem 0;
          color: white;
        }
        .rich-text-content .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 0.5rem 0;
          color: white;
        }
        .rich-text-content .ProseMirror p {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .rich-text-content .ProseMirror ul,
        .rich-text-content .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .rich-text-content .ProseMirror li {
          margin: 0.25rem 0;
        }
        .rich-text-content .ProseMirror blockquote {
          border-left: 3px solid #7F00FF;
          padding-left: 1rem;
          margin: 1rem 0;
          color: rgba(255, 255, 255, 0.8);
          font-style: italic;
        }
        .rich-text-content .ProseMirror code {
          background: rgba(127, 0, 255, 0.2);
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.9em;
        }
        .rich-text-content .ProseMirror pre {
          background: rgba(0, 0, 0, 0.5);
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .rich-text-content .ProseMirror pre code {
          background: none;
          padding: 0;
        }
        .rich-text-content .ProseMirror hr {
          border: none;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          margin: 1.5rem 0;
        }
        .rich-text-content .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }
        .rich-text-content .ProseMirror a {
          color: #00FFFF;
          text-decoration: underline;
        }
        .rich-text-content .ProseMirror a:hover {
          color: #7F00FF;
        }
      `}</style>
    </div>
  );
}

export default RichTextEditor;
